import type { ClientLog } from "./clientLog";

/** Events the global error reporting listens to. */
type GlobalErrorType = "error" | "unhandledrejection";

/** The part of `window` the global error reporting listens on. */
export interface GlobalErrorTarget {
  addEventListener(type: GlobalErrorType, listener: (event: Event) => void): void;
  removeEventListener(type: GlobalErrorType, listener: (event: Event) => void): void;
}

/**
 * Reports errors no code caught and promise rejections no code handled (F-68). Errors of scripts
 * from other origins (browser extensions) carry no error object and are left out.
 *
 * @param log - The log.
 * @param target - The window.
 * @returns A function that stops the reporting.
 */
export function reportGlobalErrors(log: ClientLog, target: GlobalErrorTarget): () => void {
  const onError = (event: Event): void => {
    const error: unknown = event instanceof ErrorEvent ? event.error : null;

    if (error !== null && error !== undefined) {
      log.error("UncaughtError", { error });
    }
  };
  const onRejection = (event: Event): void => {
    const reason: unknown = "reason" in event ? event.reason : undefined;
    log.error("UnhandledRejection", { error: reason });
  };
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);
  const stop = (): void => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };

  return stop;
}
